import {
  createBrightnessCanvas,
  createThresholdCanvas,
  createRestrictedGreysCanvas,
  createMaxSizeCanvas,
  createCroppedCanvas,
} from "./UTILS";
import { mmToPixels } from "./UTILS";
import { createEmptySheetData, getBlocksSheetData } from "./getBlocksSheetData";
import { createSharperCanvas } from "./canvasFunctions";

/*
 * A4: 297mm x 210mm
 *
 * 1 mm = 3.7795275591 pixels
 * 1 pixel = 0.26458333333
 *
 * */

// CREATE PORTRAIT PIXEL BLOCKS
export const createPixelPortraitBlocks = (inputCanvas, settings) => {
  // use a transparent bg until the eng
  const {
    contrast = 0,
    brightnessAdjust = 0,
    sharpnessAdjust = 0,
    pixelsWide = 24,
    crop,
    palette,
    removeLightestColour,
    backgroundColour = "rgba(0,0,0,0)",
  } = settings;

  const { totalColours } = palette;

  // CROP CANVAS
  const croppedCanvas = createCroppedCanvas(
    inputCanvas,
    crop,
    backgroundColour
  );

  // REDUCE TO SAME SIZE AS BLOCKS WIDE / HIGH
  const smallCanvas = createMaxSizeCanvas(croppedCanvas, pixelsWide);

  // ADD THE BRIGHTNESS ADJUSTMENT
  const brightnessCanvas = createBrightnessCanvas(smallCanvas, {
    brightnessAdjust: brightnessAdjust,
  });

  const sharperCanvas =
    sharpnessAdjust !== 0
      ? createSharperCanvas(brightnessCanvas, sharpnessAdjust)
      : brightnessCanvas;

  // ADD CONTRAST AND MAKE GREYSCALE
  const contrastCanvas = createThresholdCanvas(sharperCanvas, contrast);

  // LIMIT THE NUMBER OF COLOURS
  const { canvas: restrictedGreysCanvas, palette: greyPalette } =
    createRestrictedGreysCanvas(contrastCanvas, {
      maxGreys: totalColours,
      returnPalette: true,
    });

  const blocks = getBlocksData(restrictedGreysCanvas, {
    imagePalette: greyPalette,
    removeLightestColour,
  });

  const blocksPerImageWidth = restrictedGreysCanvas.width;
  const blocksPerImageHeight = restrictedGreysCanvas.height;

  return { blocks, blocksPerImageWidth, blocksPerImageHeight };
};

// PORTRAIT CANVAS
export const createPixelPortraitCanvas = (
  inputCanvas,
  settings,
  fullPaletteCanvas,
  sourceSwatchSize
) => {
  const {
    blockSize = 30,
    palette,
    showSheets,
    paperSize,
    sheetIndex,
    backgroundColour,
    removeLightestColour,
    countLightestColour,
    outputType,
  } = settings;

  const { blocks, blocksPerImageWidth, blocksPerImageHeight } =
    createPixelPortraitBlocks(inputCanvas, settings);

  // yellow
  // "rgb(255, 219, 88)"
  let pixelType = "square";
  if (outputType === "stickerCards" || outputType === "stickersFinished")
    pixelType = "symbol";

  const blockCanvas = createCanvasFromBlocks(blocks, {
    blocksPerImageWidth,
    blocksPerImageHeight,
    blockSize,
    palette,
    outputType,
    paperSize,
    pixelType,
    backgroundColour,
    showColourOnSheets: true,
    removeLightestColour,
    fullPaletteCanvas,
    sourceSwatchSize,
  });

  //"hsl(20,40%,100%)"

  const { lengthInMM = 297, widthInMM = 210 } = paperSize;
  const sheetWidth = mmToPixels(widthInMM);
  const sheetHeight = mmToPixels(lengthInMM);
  const blockSizeInMM = mmToPixels(blockSize);

  const sheetData = getBlocksSheetData(blocks, {
    blockSize: blockSizeInMM,
    pixelBlockSize: blockSize,
    blocksPerImageWidth,
    blocksPerImageHeight,
    paperSize,
    palette,
    removeLightestColour,
    countLightestColour,
  });

  // return a single sheet if a sheetIndex is requested
  if (sheetIndex >= 0) {
    const singleCanvas = createSingleSheetCanvas({
      blocks,
      blockSize,
      blocksPerImageWidth,
      blocksPerImageHeight,
      paperSize,
      palette,
      sheetIndex,
      removeLightestColour,
    });
    return { canvas: singleCanvas, sheetData };
  }

  if (showSheets) {
    const sheetCanvas = createSheetsPreviewCanvas(blockCanvas, {
      sheetWidth,
      sheetHeight,
      blockSize,
      blocksPerImageWidth,
      blocksPerImageHeight,
      removeLightestColour,
    });

    return { canvas: sheetCanvas, sheetData };
  }

  return { canvas: blockCanvas, sheetData };
};

// PREVIEW SHEETS CANVAS
const createSheetsPreviewCanvas = (
  inputCanvas,
  {
    blockSize,
    blocksPerImageWidth,
    blocksPerImageHeight,
    sheetWidth,
    sheetHeight,
  }
) => {
  // use the same sheet data that will be used for the actual
  // printed sheets to shwo the preview.
  const printedBlockSize = mmToPixels(blockSize);

  const { global: sheetData } = createEmptySheetData({
    blockSize: printedBlockSize,
    pixelBlockSize: blockSize,
    sheetWidth,
    sheetHeight,
    blocksPerImageWidth,
    blocksPerImageHeight,
  });

  const { sheetsWide, sheetsHigh, blocksPerSheetWidth, blocksPerSheetHeight } =
    sheetData;
  const gapSize = 15;

  const outputCanvas = document.createElement("canvas");
  outputCanvas.width = inputCanvas.width + sheetsWide * gapSize - gapSize;
  outputCanvas.height = inputCanvas.height + sheetsHigh * gapSize - gapSize;

  const previewSheetWidth = blockSize * blocksPerSheetWidth;
  const previewSheetHeight = blockSize * blocksPerSheetHeight;

  const outputCtx = outputCanvas.getContext("2d");
  // outputCtx.fillStyle = "red";
  // outputCtx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);

  for (let y = 0; y < sheetsHigh; y++) {
    for (let x = 0; x < sheetsWide; x++) {
      // const left

      const srcLeft = x * previewSheetWidth;
      const srcTop = y * previewSheetHeight;
      const destLeft = x * (previewSheetWidth + gapSize);
      const destTop = y * (previewSheetHeight + gapSize);

      outputCtx.drawImage(
        inputCanvas,
        srcLeft,
        srcTop,
        previewSheetWidth,
        previewSheetHeight,
        destLeft,
        destTop,
        previewSheetWidth,
        previewSheetHeight
      );
    }
  }

  return outputCanvas;
};

// Use the brightness of each greyscale pixel
// to select the corresponding palette index
const getBlocksData = (inputCanvas, options) => {
  let { imagePalette } = options;
  const blocks = [];

  const inputCtx = inputCanvas.getContext("2d");
  const inputWidth = inputCanvas.width;
  const inputHeight = inputCanvas.height;
  let imageData = inputCtx.getImageData(0, 0, inputWidth, inputHeight);
  let pixels = imageData.data;

  for (let i = 0; i < pixels.length; i += 4) {
    // with greyscale r, g, and b are all the same so just use r
    const brightness = pixels[i];

    // set transparent pixels to -1
    const alpha = pixels[i + 3];
    const paletteIndex = alpha < 1 ? -1 : imagePalette.indexOf(brightness);

    blocks.push(paletteIndex);
  }

  return blocks;
};

// CANVAS FROM BLOCKS
export const createCanvasFromBlocks = (blocks, options) => {
  let {
    canvasWidth,
    canvasHeight,
    blocksPerImageWidth,
    blocksPerImageHeight,
    blockSize,
    backgroundColour,
    palette,
    sheetLabel,
    removeLightestColour,
  } = options;

  const outputCanvas = document.createElement("canvas");
  const outWidth = canvasWidth ? canvasWidth : blocksPerImageWidth * blockSize;
  const outHeight = canvasHeight
    ? canvasHeight
    : blocksPerImageHeight * blockSize;

  outputCanvas.width = outWidth;
  outputCanvas.height = outHeight;

  const outputCtx = outputCanvas.getContext("2d");

  // fill background colour
  outputCtx.fillStyle = backgroundColour;
  outputCtx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);

  let x, y, i;

  // const minDiagonalFrac = 0.37;
  // const minDiagonalDistanceToEffect = maxDiagonalDistance * minDiagonalFrac;

  for (i = 0; i < blocks.length; i++) {
    x = i % blocksPerImageWidth;
    y = (i - x) / blocksPerImageWidth;

    let left = x * blockSize;
    let top = y * blockSize;

    const block = blocks[i];
    const blockTypeIndex = block;
    const lowestIndexToInclude = removeLightestColour ? 1 : 0;
    if (blockTypeIndex >= lowestIndexToInclude) {
      const swatch = palette.swatches
        ? palette.swatches[blockTypeIndex]
        : { hue: 0, saturation: 100, lightness: 50 };

      const blockHue = swatch.customHue ? swatch.customHue : swatch.hue;
      const blockSaturation = swatch.saturation;

      // if (y < 50) {
      //   blockHue = 52;
      // } else {
      //   blockHue = 140;
      //   blockSaturation -= 40;
      // }

      let blockColour = `hsl(
        ${blockHue},
        ${blockSaturation}%,
        ${swatch.lightness}%
        )`;

      drawBlock({
        outputCtx,
        left,
        top,
        blockColour,
        blockTypeIndex,
        ...options,
      });
    }
  }

  // add text showing sheet number at the top of the sheet.
  if (sheetLabel) {
    const fontSize = mmToPixels(2);

    outputCtx.fillStyle = "rgba(255,255,255,0.5)";
    outputCtx.fillRect(0, 0, fontSize * 2, fontSize);

    outputCtx.fillStyle = "rgba(0,0,0,0.8)";
    outputCtx.textBaseline = "hanging";
    outputCtx.textAlign = "left";
    outputCtx.font = `${fontSize}px Lucida Console`;
    outputCtx.fillText(sheetLabel, 0, 3);
  }

  return outputCanvas;
};

export const drawBlock = ({
  outputCtx,
  left,
  top,
  blockColour,
  blockSize,
  outputType,
  blockTypeIndex,
  fullPaletteCanvas,
  sourceSwatchSize,
}) => {
  outputCtx.save();
  outputCtx.translate(left, top);

  let drawSymbol =
    outputType === "stickersFinished" || outputType === "stickerCards";

  if (top === 0 && left === 0) {
    console.log("drawSymbol: ", drawSymbol);
  }

  if (drawSymbol) {
    drawSymbolBlock(outputCtx, blockSize, {
      fullPaletteCanvas,
      blockTypeIndex,
      outputType,
      sourceSwatchSize,
    });
  } else {
    drawSquareBlock(
      outputCtx,
      blockColour,
      blockSize,
      outputType,
      blockTypeIndex
    );
  }

  outputCtx.restore();
};

const drawSymbolBlock = (ctx, size, loopVars) => {
  const { fullPaletteCanvas, blockTypeIndex, outputType, sourceSwatchSize } =
    loopVars;

  if (!fullPaletteCanvas) return;

  const srcX = blockTypeIndex * sourceSwatchSize;
  const srcY = outputType === "stickersFinished" ? 0 : sourceSwatchSize;

  ctx.drawImage(
    fullPaletteCanvas,
    srcX,
    srcY,
    sourceSwatchSize,
    sourceSwatchSize,
    0,
    0,
    size,
    size
  );
};

// SQUARE BLOCK
export const drawSquareBlock = (
  ctx,
  colour,
  size,
  outputType,
  blockTypeIndex
) => {
  if (outputType === "paintCards") {
    ctx.fillStyle = "#aaa";
    ctx.textBaseline = "middle";
    ctx.font = `normal ${size / 1.5}px Arial`;
    ctx.textAlign = "center";

    ctx.fillText(blockTypeIndex + 1, size / 2, size / 2, size);
    ctx.strokeStyle = "#aaa";
    ctx.strokeRect(0, 0, size, size);
  } else {
    ctx.fillStyle = colour;
    ctx.fillRect(0, 0, size, size);
  }
};

// GET SINGLE SHEET CANVAS
// Used for creating the files for download
export const createSingleSheetCanvas = ({
  blocks,
  blockSize,
  blocksPerImageWidth,
  blocksPerImageHeight,
  paperSize,
  palette,
  sheetIndex = 0,
  sheetData,
  blockSizeInMM,
  outputType,
  removeLightestColour,
  fullPaletteCanvas,
  sourceSwatchSize,
}) => {
  // if blockSizeInMM no provided work it out
  blockSizeInMM = blockSizeInMM ? blockSizeInMM : mmToPixels(blockSize);

  // if sheetData not provided genenerate it
  sheetData = sheetData
    ? sheetData
    : getBlocksSheetData(blocks, {
        blocksPerImageWidth,
        blocksPerImageHeight,
        paperSize,
        blockSize: blockSizeInMM,
        palette,
      });

  const sheet = sheetData.sheets[sheetIndex];

  const singleCanvas = createCanvasFromBlocks(sheet.blocks, {
    paperSize,
    blocksPerImageWidth: sheet.lastColIndex - sheet.firstColIndex + 1,
    blocksPerImageHeight: sheet.lastRowIndex - sheet.firstRowIndex + 1,
    blockSize: sheetData.global.blockSize,
    backgroundColour: "#fff",
    outputType,
    showColourOnSheets: false,
    includeSheetNumber: true,
    sheetLabel: `[${sheetIndex + 1}]`,
    palette,
    removeLightestColour,
    fullPaletteCanvas,
    sourceSwatchSize,
  });

  return singleCanvas;
};

// LABEL BLOCK
// const drawLabelBlock = ({ ctx, colour, size, label, forceTextColour }) => {
//   const x = 0;
//   const y = 0;
//   const halfSize = size / 2;
//   const thirdSize = size / 3;
//   const twoThirdSize = thirdSize * 2;
//   ctx.fillStyle = colour;
//   ctx.fillRect(x, y, size, size);

//   const { h, s, l } = getHSL(colour);

//   const textDiff = 10;

//   const backgroundIsLight = l >= 25;
//   let textLightness = backgroundIsLight ? l - textDiff : l + 7;
//   if (l <= 10) textLightness = 21;
//   if (l <= 7) textLightness = 20;
//   if (l <= 3) textLightness = 19;
//   if (l <= 1) textLightness = 18;

//   const textColour = forceTextColour
//     ? forceTextColour
//     : `hsl(${h}, ${s}%, ${textLightness}%)`;

//   ctx.fillStyle = textColour;
//   ctx.textBaseline = "middle";
//   ctx.textAlign = "center";
//   ctx.font = `${halfSize}px Arial Black`;

//   ctx.lineWidth = 0.4;
//   ctx.strokeStyle = textColour;
//   ctx.strokeRect(x, y, size, size);

//   if (!label) label = "YO";

//   const isSingleWord = label.indexOf(" ") === -1;

//   const textPadding = size / 10;
//   const doublePadding = textPadding * 2;
//   const textX = x + halfSize + 1;

//   // draw background box
//   ctx.lineWidth = 1;
//   ctx.beginPath();
//   ctx.setLineDash([3, 2]);
//   ctx.moveTo(x + twoThirdSize, y);
//   ctx.lineTo(x + size, y + thirdSize);
//   ctx.moveTo(x + size, y + twoThirdSize);
//   ctx.lineTo(x + twoThirdSize, y + size);
//   ctx.moveTo(x + thirdSize, y + size);
//   ctx.lineTo(x, y + twoThirdSize);
//   ctx.moveTo(x, y + thirdSize);
//   ctx.lineTo(x + thirdSize, y);
//   ctx.stroke();

//   if (isSingleWord) {
//     // add text
//     const textY = y + halfSize;
//     ctx.fillText(label, textX, textY, size - doublePadding);
//   } else {
//     // if there are two words
//     const words = label.split(" ");
//     let textY = y + halfSize / 2 + 2;

//     const upperFontSize = halfSize * 0.8;
//     ctx.font = `${upperFontSize}px Arial Black`;
//     ctx.fillText(words[0], textX, textY, size - doublePadding);

//     textY += upperFontSize;
//     ctx.font = `${halfSize}px Arial Black`;
//     ctx.fillText(words[1], textX, textY, size - doublePadding);
//   }
// };

// const underline = (ctx, text, x, y, size, color, thickness, offset) => {
//   const width = ctx.measureText(text).width;

//   switch (ctx.textAlign) {
//     case "center":
//       x -= width / 2;
//       break;
//     case "right":
//       x -= width;
//       break;
//     default:
//       break;
//   }

//   y += size + offset;

//   ctx.beginPath();
//   ctx.strokeStyle = color;
//   ctx.lineWidth = thickness;
//   ctx.moveTo(x, y);
//   ctx.lineTo(x + width, y);
//   ctx.stroke();
// };

// const drawIconBlock2 = ({
//   ctx,
//   colour,
//   size,
//   label,
//   forceTextColour,
// }) => {
//   const x = 0;
//   const y = 0;
//   const halfSize = size / 2;
//   const thirdSize = size / 3;
//   const twoThirdSize = thirdSize * 2;
//   ctx.fillStyle = colour;
//   ctx.fillRect(x, y, size, size);

//   const { h, s, l } = getHSL(colour);

//   const textDiff = 10;

//   const backgroundIsLight = l >= 25;
//   let textLightness = backgroundIsLight ? l - textDiff : l + 7;
//   if (l <= 10) textLightness = 21;
//   if (l <= 7) textLightness = 20;
//   if (l <= 3) textLightness = 19;
//   if (l <= 1) textLightness = 18;

//   const textColour = forceTextColour
//     ? forceTextColour
//     : `hsl(${h}, ${s}%, ${textLightness}%)`;

//   ctx.fillStyle = textColour;
//   ctx.textBaseline = "middle";
//   ctx.textAlign = "center";
//   ctx.font = `${halfSize}px Arial Black`;

//   ctx.lineWidth = 0.4;
//   ctx.strokeStyle = textColour;
//   ctx.strokeRect(x, y, size, size);

//   if (!label) label = "YO";

//   const isSingleWord = label.indexOf(" ") === -1;

//   const textPadding = size / 10;
//   const doublePadding = textPadding * 2;
//   const textX = x + halfSize + 1;

//   // draw background box
//   ctx.lineWidth = 1;
//   ctx.beginPath();
//   ctx.setLineDash([3, 2]);
//   ctx.moveTo(x + twoThirdSize, y);
//   ctx.lineTo(x + size, y + thirdSize);
//   ctx.moveTo(x + size, y + twoThirdSize);
//   ctx.lineTo(x + twoThirdSize, y + size);
//   ctx.moveTo(x + thirdSize, y + size);
//   ctx.lineTo(x, y + twoThirdSize);
//   ctx.moveTo(x, y + thirdSize);
//   ctx.lineTo(x + thirdSize, y);
//   ctx.stroke();

//   if (isSingleWord) {
//     // add text
//     const textY = y + halfSize;
//     ctx.fillText(label, textX, textY, size - doublePadding);
//   } else {
//     // if there are two words
//     const words = label.split(" ");
//     let textY = y + halfSize / 2 + 2;

//     const upperFontSize = halfSize * 0.8;
//     ctx.font = `${upperFontSize}px Arial Black`;
//     ctx.fillText(words[0], textX, textY, size - doublePadding);

//     textY += upperFontSize;
//     ctx.font = `${halfSize}px Arial Black`;
//     ctx.fillText(words[1], textX, textY, size - doublePadding);
//   }
// };

// SYMBOL BLOCK
// const getHSL = (hslColour) => {
//   const sep = hslColour.indexOf(",") > -1 ? "," : " ";
//   const hsl = hslColour.substr(4).split(")")[0].split(sep);

//   const h = parseInt(hsl[0]);
//   const s = parseInt(hsl[1].substr(0, hsl[1].length - 1));
//   const l = parseInt(hsl[2].substr(0, hsl[2].length - 1));

//   return { h, s, l };
// };
